import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  DirectIndexBuilderDraftV1,
  DirectIndexBuilderDraftOwner,
} from "../lib/builder-draft";

export interface BuilderDraftScope {
  readonly ownerKey: string;
  readonly draft: DirectIndexBuilderDraftV1 | null;
}

interface BuilderDraftSessionState {
  readonly hasPreservedGuestDraft: boolean;
  readonly scope: BuilderDraftScope | null;
}

type BuilderDraftSessionAction =
  | {
      readonly type: "replace";
      readonly hasPreservedGuestDraft: boolean;
      readonly scope: BuilderDraftScope | null;
    }
  | {
      readonly type: "set-draft";
      readonly ownerKey: string;
      readonly draft: DirectIndexBuilderDraftV1;
    }
  | { readonly type: "clear"; readonly ownerKey: string };

const INITIAL_BUILDER_DRAFT_SESSION_STATE: BuilderDraftSessionState = {
  hasPreservedGuestDraft: false,
  scope: null,
};

function builderDraftSessionReducer(
  state: BuilderDraftSessionState,
  action: BuilderDraftSessionAction,
): BuilderDraftSessionState {
  switch (action.type) {
    case "replace":
      return {
        hasPreservedGuestDraft: action.hasPreservedGuestDraft,
        scope: action.scope,
      };
    case "set-draft":
      return {
        ...state,
        scope: { ownerKey: action.ownerKey, draft: action.draft },
      };
    case "clear":
      return {
        hasPreservedGuestDraft: false,
        scope: { ownerKey: action.ownerKey, draft: null },
      };
  }
}

function useCreateDraftSession() {
  const previousOwnerRef = useRef<DirectIndexBuilderDraftOwner | null>(null);
  const attemptedGuestClaimOwnerKeyRef = useRef<string | null>(null);
  const [storedDraftScope, setStoredDraftScope] =
    useState<BuilderDraftScope | null>(null);
  const [draftSession, dispatchDraftSession] = useReducer(
    builderDraftSessionReducer,
    INITIAL_BUILDER_DRAFT_SESSION_STATE,
  );
  return useMemo(
    () => ({
      previousOwnerRef,
      attemptedGuestClaimOwnerKeyRef,
      storedDraftScope,
      setStoredDraftScope,
      draftSession,
      dispatchDraftSession,
    }),
    [draftSession, storedDraftScope],
  );
}

const BuilderDraftSessionContext = createContext<ReturnType<
  typeof useCreateDraftSession
> | null>(null);

function BuilderDraftSessionRoot(props: { readonly children: ReactNode }) {
  const session = useCreateDraftSession();
  return (
    <BuilderDraftSessionContext.Provider value={session}>
      {props.children}
    </BuilderDraftSessionContext.Provider>
  );
}

export function BuilderDraftSessionProvider(props: {
  readonly children: ReactNode;
  readonly inherit?: boolean;
}) {
  const inherited = useContext(BuilderDraftSessionContext);
  return props.inherit && inherited ? (
    props.children
  ) : (
    <BuilderDraftSessionRoot>{props.children}</BuilderDraftSessionRoot>
  );
}

export function useBuilderDraftSession() {
  const session = useContext(BuilderDraftSessionContext);
  if (!session) throw new Error("Builder draft session provider is missing");
  return session;
}
